import React, { useState, useEffect, useRef } from 'react';
import { Button, Form, Table, Badge, Spinner, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import { BiDownload, BiUpload, BiPlus, BiTrash, BiSend, BiSearch } from 'react-icons/bi'; // BiSearch used in toolbar
import ReactPaginate from 'react-paginate';
import { TemplateRespondentDto, TemplateRespondentPagination } from '../../types/template';
import Swal from 'sweetalert2';

interface Props {
    templateCode: string;
    token: string;
}

interface ManualForm {
    employeeId: string;
    name: string;
    email: string;
}


const PAGE_SIZE = 20;

const SOURCE_LABEL: Record<number, { label: string; bg: string }> = {
    1: { label: 'Manual', bg: 'secondary' },
    2: { label: 'Excel',  bg: 'info'      },
    3: { label: 'All',    bg: 'primary'   },
};

export default function FormRespondents({ templateCode, token }: Props) {
    const [data, setData]         = useState<TemplateRespondentPagination | null>(null);
    const [loading, setLoading]   = useState(false);
    const [page, setPage]         = useState(0);
    const [keyword, setKeyword]   = useState('');
    const [empInput, setEmpInput] = useState('');
    const [adding, setAdding]     = useState(false);
    const [sending, setSending]   = useState(false);

    // Manual-add modal (when employee not in DB)
    const [showModal, setShowModal]   = useState(false);
    const [manualForm, setManualForm] = useState<ManualForm>({ employeeId: '', name: '', email: '' });
    const [manualSaving, setManualSaving] = useState(false);
    const [lookupSource, setLookupSource] = useState<'azure' | null>(null);


    const fileRef = useRef<HTMLInputElement>(null);
    const auth    = () => ({ headers: { Authorization: `Bearer ${token}` } });
    const api     = process.env.REACT_APP_API_END_POINT;

    // ── Data loading ───────────────────────────────────────────────────────────
    const load = async (p = 0, kw = keyword) => {
        if (!templateCode || !token) return;
        setLoading(true);
        try {
            const res = await axios.get<TemplateRespondentPagination>(
                `${api}/v1/Form/respondents`,
                { ...auth(), params: { code: templateCode, keyword: kw, offset: p * PAGE_SIZE, limit: PAGE_SIZE } }
            );
            setData(res.data);
        } catch {
            toast.error('Failed to load respondents');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(0, ''); }, [templateCode, token]); // eslint-disable-line

    const handleSearch = () => { setPage(0); load(0, keyword); };

    // ── Add employee (lookup local DB → Azure AD → manual) ────────────────────
    const handleAdd = async () => {
        const id = empInput.trim();
        if (!id) return;
        setAdding(true);
        try {
            const { data: lookup } = await axios.get<{
                found: boolean;
                source: 'db' | 'azure' | null;
                employeeId: string;
                name?: string;
                email?: string;
            }>(
                `${api}/v1/Form/respondents/lookup`,
                { ...auth(), params: { employeeId: id } }
            );

            if (lookup.found && lookup.source === 'db') {
                // Found in local DB → add directly (no confirmation needed)
                await axios.post(
                    `${api}/v1/Form/respondents/add`,
                    { templateCode, employeeId: id, addAll: false },
                    { ...auth(), headers: { ...auth().headers, 'Content-type': 'application/json' } }
                );
                setEmpInput('');
                toast.success(`${lookup.name ?? id} added`);
                load(page);
            } else if (lookup.found && lookup.source === 'azure') {
                // Found in Azure AD → pre-fill modal for confirmation
                setLookupSource('azure');
                setManualForm({ employeeId: id, name: lookup.name ?? '', email: lookup.email ?? '' });
                setShowModal(true);
            } else {
                // Not found anywhere → manual entry
                setLookupSource(null);
                setManualForm({ employeeId: id, name: '', email: '' });
                setShowModal(true);
            }
        } catch (err: any) {
            const msg = err?.response?.data?.message ?? '';
            if (msg.toLowerCase().includes('already')) {
                toast.warn(`Employee '${id}' is already a respondent`);
            } else {
                toast.error(msg || 'Failed to add respondent');
            }
        } finally {
            setAdding(false);
        }
    };

    // ── Save manual form ───────────────────────────────────────────────────────
    const handleManualSave = async () => {
        if (!manualForm.name.trim())  { toast.warn('Name is required');  return; }
        if (!manualForm.email.trim()) { toast.warn('Email is required'); return; }
        setManualSaving(true);
        try {
            await axios.post(
                `${api}/v1/Form/respondents/add`,
                { templateCode, employeeId: manualForm.employeeId, name: manualForm.name.trim(), email: manualForm.email.trim(), addAll: false },
                { ...auth(), headers: { ...auth().headers, 'Content-type': 'application/json' } }
            );
            toast.success(`${manualForm.name} added`);
            setShowModal(false);
            setEmpInput('');
            load(page);
        } catch (err: any) {
            toast.error(err?.response?.data?.message ?? 'Failed to add respondent');
        } finally {
            setManualSaving(false);
        }
    };

    // ── Add all ────────────────────────────────────────────────────────────────
    const handleAddAll = async () => {
        const result = await Swal.fire({
            title: 'Add All Employees?',
            text: 'This will add all active employees as respondents.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#c5281f',
            confirmButtonText: 'Yes, add all',
        });
        if (!result.isConfirmed) return;
        setAdding(true);
        try {
            await axios.post(
                `${api}/v1/Form/respondents/add`,
                { templateCode, addAll: true },
                { ...auth(), headers: { ...auth().headers, 'Content-type': 'application/json' } }
            );
            toast.success('All employees added');
            load(0);
        } catch (err: any) {
            toast.error(err?.response?.data?.message ?? 'Failed to add all employees');
        } finally {
            setAdding(false);
        }
    };

    // ── Import Excel ───────────────────────────────────────────────────────────
    const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append('templateCode', templateCode);
        fd.append('file', file);
        try {
            const res = await axios.post<{ success: number; fail: number; errors: string[] }>(
                `${api}/v1/Form/respondents/import`,
                fd,
                { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' } }
            );
            const { success, fail } = res.data;
            if (fail > 0) toast.warn(`Imported ${success}, skipped ${fail}`);
            else toast.success(`Imported ${success} respondents`);
            load(0);
        } catch {
            toast.error('Import failed');
        } finally {
            if (fileRef.current) fileRef.current.value = '';
        }
    };

    // ── Delete ─────────────────────────────────────────────────────────────────
    const handleDelete = async (r: TemplateRespondentDto) => {
        const result = await Swal.fire({
            title: `Remove ${r.name ?? r.employeeId}?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#c5281f',
            confirmButtonText: 'Remove',
        });
        if (!result.isConfirmed) return;
        try {
            await axios.delete(`${api}/v1/Form/respondents/delete`, { ...auth(), params: { id: r.id } });
            toast.success('Respondent removed');
            load(page);
        } catch { toast.error('Failed to remove respondent'); }
    };

    // ── Send invites ───────────────────────────────────────────────────────────
    const handleSendInvites = async () => {
        setSending(true);
        try {
            const res = await axios.post<{ sent: number; message: string }>(
                `${api}/v1/Form/sendInvites`,
                null,
                { ...auth(), params: { code: templateCode } }
            );
            toast.success(res.data.message);
        } catch (err: any) {
            toast.error(err?.response?.data?.message ?? 'Failed to send invites');
        } finally {
            setSending(false);
        }
    };

    // ── Download sample ────────────────────────────────────────────────────────
    const downloadSample = async () => {
        try {
            const res = await axios.get(`${api}/v1/Form/respondents/template`, { ...auth(), responseType: 'blob' });
            const blobUrl = URL.createObjectURL(res.data as Blob);
            const a = document.createElement('a');
            a.href = blobUrl; a.download = 'FormRespondents_sample.xlsx';
            a.click(); URL.revokeObjectURL(blobUrl);
        } catch { toast.error('Failed to download sample'); }
    };

    // ──────────────────────────────────────────────────────────────────────────

    return (
        <div className="p-4">
            {/* Toolbar */}
            <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
                {/* Search */}
                <div className="input-group" style={{ maxWidth: 300 }}>
                    <Form.Control
                        placeholder="Search name / ID / email…"
                        value={keyword}
                        onChange={e => setKeyword(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleSearch()}
                    />
                    <Button variant="outline-secondary" onClick={handleSearch}>
                        <BiSearch />
                    </Button>
                </div>

                {/* Add by Employee ID */}
                <div className="input-group" style={{ maxWidth: 280 }}>
                    <Form.Control
                        placeholder="Employee ID"
                        value={empInput}
                        onChange={e => setEmpInput(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && handleAdd()}
                    />
                    <Button variant="outline-primary" onClick={handleAdd} disabled={adding || !empInput.trim()}>
                        {adding ? <Spinner size="sm" /> : <><BiPlus /> Add</>}
                    </Button>
                </div>

                <div className="d-flex gap-2 ms-auto">
                    <Button variant="outline-secondary" size="sm" onClick={handleAddAll} disabled={adding}>
                        + All Employees
                    </Button>
                    <Button variant="outline-secondary" size="sm" onClick={() => fileRef.current?.click()}>
                        <BiUpload /> Import Excel
                    </Button>
                    <input ref={fileRef} type="file" accept=".xlsx" style={{ display: 'none' }} onChange={handleImport} />
                    <Button variant="outline-secondary" size="sm" onClick={downloadSample}>
                        <BiDownload /> Sample
                    </Button>
                    <Button
                        size="sm"
                        style={{ background: '#c5281f', border: 'none' }}
                        onClick={handleSendInvites}
                        disabled={sending}
                    >
                        {sending ? <Spinner size="sm" /> : <><BiSend /> Send Invites</>}
                    </Button>
                </div>
            </div>

            {/* Table */}
            {loading ? (
                <div className="text-center py-5"><Spinner /></div>
            ) : (
                <>
                    <Table hover className="mb-3 bg-white rounded shadow-sm" style={{ overflow: 'hidden' }}>
                        <thead className="table-light">
                            <tr>
                                <th style={{ width: 140 }}>Employee ID</th>
                                <th>Name</th>
                                <th>Email</th>
                                <th style={{ width: 100 }}>Source</th>
                                <th style={{ width: 110 }}>Responded</th>
                                <th style={{ width: 90 }}>Invited</th>
                                <th style={{ width: 50 }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {(data?.respondents ?? []).length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="text-center text-muted py-5">
                                        No respondents yet. Use <strong>+ Add</strong> or <strong>Import Excel</strong> to get started.
                                    </td>
                                </tr>
                            ) : (
                                (data?.respondents ?? []).map((r: TemplateRespondentDto) => {
                                    const src = SOURCE_LABEL[r.sourceType] ?? { label: String(r.sourceType), bg: 'light' };
                                    return (
                                        <tr key={r.id}>
                                            <td className="text-muted small">{r.employeeId}</td>
                                            <td className="fw-semibold">{r.name ?? <span className="text-muted fst-italic">—</span>}</td>
                                            <td className="small">{r.email ?? <span className="text-muted fst-italic">—</span>}</td>
                                            <td>
                                                <Badge bg={src.bg} style={{ fontSize: 11 }}>{src.label}</Badge>
                                            </td>
                                            <td>
                                                {r.hasResponded
                                                    ? <Badge bg="success">Yes</Badge>
                                                    : <Badge bg="light" text="dark">No</Badge>
                                                }
                                            </td>
                                            <td className="text-muted small">
                                                {r.inviteSentAt ? '✓ Sent' : '—'}
                                            </td>
                                            <td>
                                                <Button
                                                    size="sm" variant="link" className="p-0 text-danger"
                                                    title="Remove"
                                                    onClick={() => handleDelete(r)}
                                                >
                                                    <BiTrash />
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </Table>

                    {(data?.totalPages ?? 0) > 1 && (
                        <ReactPaginate
                            pageCount={data?.totalPages ?? 1}
                            forcePage={page}
                            onPageChange={({ selected }) => { setPage(selected); load(selected); }}
                            previousLabel="‹" nextLabel="›"
                            containerClassName="pagination justify-content-center"
                            pageClassName="page-item" pageLinkClassName="page-link"
                            previousClassName="page-item" previousLinkClassName="page-link"
                            nextClassName="page-item" nextLinkClassName="page-link"
                            activeClassName="active"
                        />
                    )}
                    <div className="text-muted small mt-2">Total: {data?.total ?? 0} respondents</div>
                </>
            )}

            {/* Manual-add modal (employee not in local DB) */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: 16 }}>
                        {lookupSource === 'azure' ? 'Confirm employee details' : 'Employee not found in system'}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {lookupSource === 'azure' ? (
                        <div className="d-flex align-items-start gap-2 mb-3 p-2 rounded" style={{ background: '#e8f5e9', fontSize: 13 }}>
                            <span style={{ color: '#2e7d32', fontSize: 16 }}>✓</span>
                            <span style={{ color: '#2e7d32' }}>
                                Found in <strong>Azure AD</strong>. Please verify the details before adding.
                            </span>
                        </div>
                    ) : (
                        <p className="text-muted mb-3" style={{ fontSize: 13 }}>
                            Employee ID <strong>{manualForm.employeeId}</strong> is not in the employee database.
                            Please fill in the details manually.
                        </p>
                    )}
                    <Form.Group className="mb-3">
                        <Form.Label>Employee ID</Form.Label>
                        <Form.Control value={manualForm.employeeId} disabled />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Full Name <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                            placeholder="e.g. John Smith"
                            value={manualForm.name}
                            onChange={e => setManualForm(f => ({ ...f, name: e.target.value }))}
                            autoFocus={lookupSource !== 'azure'}
                        />
                    </Form.Group>
                    <Form.Group className="mb-1">
                        <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                            type="email"
                            placeholder="e.g. john.smith@company.com"
                            value={manualForm.email}
                            onChange={e => setManualForm(f => ({ ...f, email: e.target.value }))}
                            onKeyDown={e => e.key === 'Enter' && handleManualSave()}
                        />
                    </Form.Group>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setShowModal(false)}>Cancel</Button>
                    <Button
                        style={{ background: '#c5281f', border: 'none' }}
                        onClick={handleManualSave}
                        disabled={manualSaving}
                    >
                        {manualSaving ? <Spinner size="sm" /> : 'Add Respondent'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}
